import React, { useEffect, useState } from "react";
import { Table, Button, Space, Modal, Form, Input, InputNumber, Select, Switch, Card } from "antd";
import { EditOutlined, DeleteOutlined, PlusOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const { Option } = Select;
const { TextArea } = Input;

const ProductManagement = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form] = Form.useForm();
  const [categories, setCategories] = useState([]);
  const [publishers, setPublishers] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchPublishers();
    fetchCurrencies();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const response = await axios.get("super-admin/products");
      setProducts(response.data);
    } catch (error) {
      toast.error("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get("super-admin/product-categories");
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchPublishers = async () => {
    try {
      const response = await axios.get("super-admin/book-publishers");
      setPublishers(response.data);
    } catch (error) {
      console.error("Failed to fetch publishers:", error);
    }
  };

  const fetchCurrencies = async () => {
    try {
      const response = await axios.get("super-admin/product-currencies");
      setCurrencies(response.data);
    } catch (error) {
      console.error("Failed to fetch currencies:", error);
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingProduct(record);
    form.setFieldsValue({
      ...record,
      product_category_id: record.product_category_id || undefined,
      book_publisher_id: record.book_publisher_id || undefined,
      product_currency_id: record.product_currency_id || undefined,
    });
    setModalVisible(true);
  };

  const handleDelete = async (record) => {
    try {
      await axios.delete(`super-admin/products/${record.id}`);
      toast.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingProduct) {
        await axios.put(`super-admin/products/${editingProduct.id}`, values);
        toast.success("Product updated successfully");
      } else {
        await axios.post("super-admin/products", values);
        toast.success("Product created successfully");
      }
      setModalVisible(false);
      form.resetFields();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to save product");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "ISBN",
      dataIndex: "isbn",
      key: "isbn",
    },
    {
      title: "Author",
      dataIndex: "author",
      key: "author",
    },
    {
      title: "Sale Price",
      dataIndex: "sale_price",
      key: "sale_price",
      render: (value) => `₹${parseFloat(value || 0).toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (status ? "Active" : "Inactive"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate("/super-admin/dashboard")}
        style={{ marginBottom: "16px" }}
      >
        Back to Dashboard
      </Button>
      <Card
        title="Product Management"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            Create Product
          </Button>
        }
      >
        <Table
          dataSource={products}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingProduct ? "Edit Product" : "Create Product"}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: "Please input product name!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="isbn"
            label="ISBN"
            rules={[{ required: true, message: "Please input ISBN!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="author" label="Author">
            <Input />
          </Form.Item>
          <Form.Item name="sku" label="SKU">
            <TextArea rows={2} />
          </Form.Item>
          <Form.Item name="purchase_price" label="Purchase Price">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="sale_price"
            label="Sale Price"
            rules={[{ required: true, message: "Please input sale price!" }]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="product_category_id" label="Product Category">
            <Select placeholder="Select category" allowClear>
              {categories.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="book_publisher_id" label="Book Publisher">
            <Select placeholder="Select publisher" allowClear>
              {publishers.map((pub) => (
                <Option key={pub.id} value={pub.id}>
                  {pub.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="product_currency_id" label="Product Currency">
            <Select placeholder="Select currency" allowClear>
              {currencies.map((curr) => (
                <Option key={curr.id} value={curr.id}>
                  {curr.name} ({curr.symbol})
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="unit_measurement" label="Unit Measurement">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="unit_type" label="Unit Type">
            <Input />
          </Form.Item>
          <Form.Item name="reorder_quantity" label="Reorder Quantity">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="status" label="Status" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductManagement;
